import { useCallback } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { Box, InputAdornment, TextField, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { TProjectFormData } from 'src/@types';
import ExcelFormButtons from 'src/components/ExcelFormButtons';
import { RHFDateWithCheckbox, RHFTextField } from 'src/components/hook-form';
import ItemActionMenu from 'src/components/ItemActionMenu';
import { useExcelFormField } from 'src/hooks/useExcelFormField';
import { numberToSimpleKoreanCurrency } from 'src/utils/koreanCurrency';
import {
  downloadPurchaseOtherTemplate,
  parsePurchaseOtherExcel,
  PurchaseOtherRow,
} from 'src/utils/projectExcelUtils';

export interface PurchaseOtherSectionProps {
  methods: UseFormReturn<TProjectFormData>;
  readonly?: boolean;
}

type FormRow = NonNullable<TProjectFormData['purchaseOthers']>[number];

/**
 * 그 외용 매입내역 섹션 컴포넌트
 * - 항목, 업체, 세금계산서 발행일/발행여부, 대금 지급일/지급여부, 비고
 */
function PurchaseOtherSection({ methods, readonly }: PurchaseOtherSectionProps) {
  const { enqueueSnackbar } = useSnackbar();

  const {
    fields: purchaseOther,
    append: appendPurchaseOther,
    insert: insertPurchaseOther,
    remove: removePurchaseOther,
    fileInputRef,
    handleDownload,
    handleUpload,
    handleClickUpload,
  } = useExcelFormField<TProjectFormData, 'purchaseOthers', PurchaseOtherRow, FormRow>({
    methods,
    fieldName: 'purchaseOthers',
    downloadFn: downloadPurchaseOtherTemplate,
    parseFn: parsePurchaseOtherExcel,
    formToExcel: (item) => ({
      category: item.category || '',
      vender: item.vender || '',
      purchaseAmt: item.purchaseAmt || 0,
      issueDay: item.issueDay || '',
      issueYn: item.issueYn || false,
      paymentDay: item.paymentDay || '',
      paymentYn: item.paymentYn || false,
      memo: item.memo || null,
    }),
    excelToForm: (row) => ({
      category: row.category,
      vender: row.vender,
      purchaseAmt: row.purchaseAmt || 0,
      issueDay: row.issueDay || null, // 이미 timestamp 또는 null
      issueYn: row.issueYn,
      paymentDay: row.paymentDay || Date.now(), // 이미 timestamp
      paymentYn: row.paymentYn,
      memo: row.memo || '',
    }),
    labels: {
      download: '매입내역',
      upload: '건의 매입내역이 업로드되었습니다.',
      empty: '업로드할 데이터가 없습니다.',
    },
  });

  // 매입내역 복사 함수
  const handleCopyItem = useCallback(
    (index: number) => {
      const currentValues = methods.getValues('purchaseOthers');
      const itemToCopy = currentValues?.[index];

      if (itemToCopy) {
        const copiedItem = {
          category: itemToCopy.category || '',
          vender: itemToCopy.vender || '',
          purchaseAmt: itemToCopy.purchaseAmt || 0,
          issueDay: itemToCopy.issueDay ?? null,
          issueYn: false,
          paymentDay: itemToCopy.paymentDay || Date.now(),
          paymentYn: false,
          memo: itemToCopy.memo || '',
        };

        insertPurchaseOther(index + 1, copiedItem);
        enqueueSnackbar('매입내역이 복사되었습니다.', { variant: 'success' });
      }
    },
    [insertPurchaseOther, methods, enqueueSnackbar],
  );

  const handleAddItem = useCallback(() => {
    appendPurchaseOther({
      category: '',
      vender: '',
      purchaseAmt: 0,
      issueDay: null,
      issueYn: false,
      paymentDay: Date.now(),
      paymentYn: false,
      memo: '',
    });
  }, [appendPurchaseOther]);

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {!readonly && (
        <ExcelFormButtons
          fileInputRef={fileInputRef}
          onDownload={handleDownload}
          onClickUpload={handleClickUpload}
          onUpload={handleUpload}
          onAdd={handleAddItem}
          addLabel="매입내역 추가"
        />
      )}
      {purchaseOther.length === 0 ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{
            p: 3,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            color: 'text.secondary',
          }}
        >
          <Typography variant="body2">매입내역이 없습니다.</Typography>
        </Box>
      ) : (
        purchaseOther.map((field, index) => (
          <Box
            key={field.id}
            display="flex"
            flexDirection="column"
            gap={2}
            sx={{
              p: 2,
              pl: readonly ? 2 : 4,
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 1,
              position: 'relative',
            }}
          >
            {!readonly && (
              <ItemActionMenu
                onDelete={() => removePurchaseOther(index)}
                onCopy={() => handleCopyItem(index)}
                deleteLabel="삭제"
                copyLabel="복사"
              />
            )}

            {/* 첫 번째 줄: 항목 + 업체 + 매입금액 + 비고 */}
            <Box display="flex" flexDirection="row" flexWrap="wrap" gap={2} alignItems="flex-start">
              <RHFTextField
                name={`purchaseOthers.${index}.category`}
                label="항목"
                inputProps={{ readOnly: readonly, maxLength: 100 }}
                sx={{ flex: 1, minWidth: 150 }}
              />
              <RHFTextField
                name={`purchaseOthers.${index}.vender`}
                label="업체"
                inputProps={{ readOnly: readonly, maxLength: 100 }}
                sx={{ flex: 1, minWidth: 150 }}
              />
              <Controller
                name={`purchaseOthers.${index}.purchaseAmt`}
                control={methods.control}
                render={({ field: { ref, ...field }, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label="매입금액"
                    value={field.value ? field.value.toLocaleString() : ''}
                    placeholder="매입금액"
                    error={!!error}
                    onChange={(e) => {
                      const value = e.target.value;
                      const numericValue = Number(value.replace(/,/g, ''));
                      if (!isNaN(numericValue)) {
                        field.onChange(numericValue);
                      }
                    }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">원</InputAdornment>,
                      readOnly: readonly,
                    }}
                    helperText={
                      error?.message ||
                      (field.value ? numberToSimpleKoreanCurrency(field.value) : '')
                    }
                    inputRef={ref}
                    sx={{ flex: 1, minWidth: 150 }}
                  />
                )}
              />
              <RHFTextField
                name={`purchaseOthers.${index}.memo`}
                label="비고"
                multiline
                inputProps={{ readOnly: readonly }}
                sx={{ flex: 2, minWidth: 200 }}
              />
            </Box>

            {/* 두 번째 줄: 세금계산서 발행일 + 대금 지급일 */}
            <Box display="flex" flexDirection="row" flexWrap="wrap" gap={2}>
              <RHFDateWithCheckbox
                dateName={`purchaseOthers.${index}.issueDay`}
                checkboxName={`purchaseOthers.${index}.issueYn`}
                label="세금계산서 발행 예정일"
                checkedLabel="세금계산서 발행일"
                checkboxLabel="발행 여부"
                readOnly={readonly}
                clearable
                sx={{ flex: 1, minWidth: 250 }}
              />
              <RHFDateWithCheckbox
                dateName={`purchaseOthers.${index}.paymentDay`}
                checkboxName={`purchaseOthers.${index}.paymentYn`}
                label="대금 지급 예정일"
                checkedLabel="대금 지급일"
                checkboxLabel="지급 여부"
                readOnly={readonly}
                sx={{ flex: 1, minWidth: 250 }}
              />
            </Box>
          </Box>
        ))
      )}
    </Box>
  );
}

export default PurchaseOtherSection;
